/**
 * Theme adapter for OSW US site — injects real next/font families into the foundation.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types';
import { nHaasGroteskTxProFont, neueAachenProRegularFont, nHaasGroteskTxProFontRegular } from '../common/nextFont';
import {
  buildThemeVarsUSOsw as buildThemeVarsUSOswFoundation,
  buildThemeMixinsUSOsw as buildThemeMixinsUSOswFoundation,
  buildThemeWidgetStylesUSOsw as buildThemeWidgetStylesUSOswFoundation,
  buildThemeWidgetStylesUSOswFromBase,
  buildThemeWidgetStylesUsLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsUSOsw = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsUSOswFoundation> =>
  buildThemeVarsUSOswFoundation(vars, {
    neueAachenPro: neueAachenProRegularFont.style.fontFamily,
    neueHaasGroteskTextPro: nHaasGroteskTxProFont.style.fontFamily,
    neueHaasGroteskTextProRegular: nHaasGroteskTxProFontRegular.style.fontFamily,
  });

export const buildThemeMixinsUSOsw = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsUSOswFoundation> => buildThemeMixinsUSOswFoundation(vars);

export const buildThemeWidgetStylesUSOsw = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesUSOswFoundation> => buildThemeWidgetStylesUSOswFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSOswFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSOswFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSOswFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSOswFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUSOswFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUSOsw, buildThemeMixinsUSOsw, buildThemeWidgetStylesUSOsw);
export default createTheme;
